import { dateUtil } from "src/app/support/date-util";
import { Address } from "../address";
import { InvoiceItem, InvoiceItemType, InvoiceItemUnit } from "./invoice.item";
import { isNull, isUndefined, values } from "lodash-es";
import { sntTranslate } from "src/app/support/translate";
import { VatNatureType } from "src/app/backend/models/vat-nature-type";
import Message from "src/app/backend/models/tessera-sanitaria/message";
import { FiscalProfile } from "src/app/backend/models/fiscal-profile/fiscal-profile";
import { PaymentMethod } from "src/app/backend/models/payment-method/payment-method";
import { CustomerLight } from "src/app/backend/models/customer/customer";
import {
  BookItem,
  BookItemCreate,
  BookItemType,
  bookItemUnitToInvoiceItemUnit
} from "src/app/backend/models/book/book-item";
import { PetLight } from "src/app/backend/models/pet/pet";
import { RateTableLight } from "src/app/backend/models/rate-table/rate-table";
import { MedicalServiceLight } from "src/app/backend/models/medical-service";

export enum InvoiceExportType {
  INVOICE = "invoice",
  INVOICE_ITEM = "invoice-item",
  TEAM_SYSTEM = "team-system",
  ZUCCHETTI_AD_HOC = "zucchetti-ad-hoc",
  DATEV_KOINOS_COGE = "datev-koinos-coge",
  INVOICE_DRAFT = "invoice-draft",
  PAYMENT = "payment"
}

export enum InvoiceFiscalProfileTaxRegime {
  NORMAL_REGIME = "normal-regime",
  FLAT_TAX_REGIME = "flat-tax-regime",
  FACILITATED_TAX_SYSTEM = "facilitated-tax-system"
}

export enum InvoiceType {
  SALES_INVOICE = "sales-invoice",
  CREDIT_NOTE = "credit-note",
  ADVANCE_INVOICE = "advance-invoice",
  FE_SALES_INVOICE = "fe-sales-invoice",
  FE_CREDIT_NOTE = "fe-credit-note",
  ADVANCE_RECEIPT = "advance-receipt"
}

export enum InvoicePaymentTerm {
  DELAYED_PAYMENT = "delayed-payment",
  THIRTY_DAYS_PAYMENT = "thirty-days-payment",
  SIXTY_DAYS_PAYMENT = "sixty-days-payment",
  NINETY_DAYS_PAYMENT = "ninety-days-payment",
  INSTALLMENT_PLAN = "installment-plan",
  DIRECT_REMITTACE = "direct-remittance",
  ADVANCE_PAYMENT = "advance-payment"
}

export enum InvoicePaymentMethod {
  CASH = "cash",
  CHECKS = "checks",
  BANK_TRANSFER = "bank-transfer",
  DEBIT_CARD = "debit-card",
  CREDIT_CARD = "credit-card",
  SATISPAY_BUSINESS = "satispay-business"
}

export interface InvoiceFiscalProfile {
  id: string;
  name: string;
  type: string;
  sectional?: {
    id?: string;
    name?: string;
    sequence: string;
    year?: boolean;
    prefix?: boolean;
  };
  firstName?: string;
  lastName?: string;
  businessName?: string;
  vatNumber?: string;
  ssn?: string;
  email?: string;
  pec?: string;
  professionalRegister?: string;
  professionalRegisterProvince?: string;
  professionalRegisterNumber?: string;
  professionalRegisterSignedAt?: Date;
  professionalTitle?: string;
  eoriCode?: string;
  profession: string;
  vat?: number;
  vatPeriodicity?: string;
  taxRegime: InvoiceFiscalProfileTaxRegime;
  withholdingTax: number;
  stampTax: number;
  address: Address;

  enpav: {
    rate: number;
    drugs: boolean;
    products: boolean;
  };

  enableCommercialRounding?: boolean;
}

export interface InvoicePaymentInstallment {
  amount: number;
  term: string;
  expiresAt: string;
  balancedAt?: string;
  balanceAccount?: string;
  revenueCenter?: string;
  note?: string;
}

export interface InvoicePaymentRecepitAdvanceInvoice {
  id: string;
  number: string;
  emittedAt: string | undefined;
}

export interface InvoicePaymentRecepit {
  id: string;
  createdAt: Date;
  createdBy: { id: string; name: string };
  updatedAt?: string;
  amount: number;
  paidAt: Date;
  method?: InvoicePaymentMethod;
  paymentMethod?: PaymentMethod;
  advanceInvoice?: InvoicePaymentRecepitAdvanceInvoice | undefined;
}

export interface InvoicePaymentRecepitCreate {
  amount: number;
  paidAt: Date;
  method: InvoicePaymentMethod;
  advanceInvoiceId?: string;
}

export interface InvoiceVatSummaryLine {
  tax: number;
  detail: {
    rateable: number;
    taxAmount: number;
    exemptionReasons?: string[];
    vatNatureList?: VatNatureType[];
  };
}

export type InvoiceSendingReceiptModel = {
  storageId?: string;
  label?: string;
  fileName?: string;
  insertedAt?: Date;
  type?: string;
  description?: string;
  note?: string;
  sdiId?: string;
  hash?: string;
};

export type InvoiceSendingOutcomeModel = {
  storageId?: string;
  label?: string;
  fileName?: string;
  insertedAt?: Date;
  type?: string;
  description?: string;
  note?: string;
  sdiId?: string;
  hash?: string;
};

export type InvoicePaidStatus = "all" | "paid" | "unpaid" | null;

export type InvoiceStatus = "draft" | "emitted";

export type InvoiceSendingStatus =
  | "processing"
  | "sent"
  | "accepted"
  | "discarded"
  | "warning"
  | "not-sent"
  | "rejected";

export type InvoiceSending = {
  id: string;
  number?: string;
  status: InvoiceSendingStatus;
  messages?: Message[];
  type: "tessera-sanitaria" | "fatturazione-elettronica" | "electronic-invoice";
  serial?: string;
  receipt?: InvoiceSendingReceiptModel;
  outcome?: InvoiceSendingOutcomeModel;
};

export type InvoiceAdditionalGeneralDataType =
  | "none"
  | "purchase-order"
  | "contract"
  | "agreement"
  | "receiption";

export type InvoiceAdditionalGeneralData = {
  type?: InvoiceAdditionalGeneralDataType;
  documentNumber?: string;
  documentDate?: Date;
  lineNumbers?: number[];
  itemNumber?: string;
  contractOrAgreementCode?: string;
  cig?: string;
  cup?: string;
};

export type InvoiceCustomer = {
  id: string;
  type: "business" | "individual" | "association" | "public";
  withoutSsn?: boolean;
  extraUe?: boolean;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  ssn?: string;
  vatNumber?: string;
  address: Address;
  iban?: string;
  destinationCode?: string;
};

export interface Invoice {
  id: string;
  createdAt: string;
  deleted: boolean;
  deletedAt?: string;
  createdBy?: { id: string; name: string };
  externalId?: string;
  number: string;
  type: InvoiceType;
  fiscalProfile: InvoiceFiscalProfile;
  vatSplit?: boolean;
  stampTaxAllowed?: boolean;
  stampTax?: boolean;
  stampTaxAmount?: number;
  customer?: InvoiceCustomer | null;
  customerAdministrativeReference?: string;
  patient?: Partial<PetLight>;
  status?: string;
  creditNote?: string;
  debitNote?: string;
  reversedAt?: Date;
  reversedBy?: string;
  reversedInvoice?: {
    emittedAt: string;
    id: string;
    number: string;
  };
  emittedAt?: string;
  emittedBy?: {
    id: string;
    name: string;
  };
  deliveredAt?: Date;
  note?: string;
  enpavRateableTotal: number; // Imponibile ENPAV
  enpavAmount: number; // Importo Enpav
  enpavAdministrativeReference?: string;
  withholdingTaxRateableTotal: number; // Imponibile Ritenuta d'acconto
  withholdingTaxAmount: number; // Importo Ritenuta d'acconto

  vatRateableTotal: number | undefined;
  rateableTotal: number; // Imponibile in fattura

  vatTaxAmount: number; // Importo IVA
  taxesAmount: number; // Totale tassa in fattura
  taxedTotal: number; // Totale in fattura (IVA + ENPAV + RA)
  discount?: number; // Sconto in fattura
  discountedTotal: number; // Totale scontato in fattura
  adjustment?: number; // Squadratura
  adjustedTotal: number; // Totale da pagare
  paidTotal: number;
  items: InvoiceItem[];
  vatSummaryLines: InvoiceVatSummaryLine[];
  paymentTerm: InvoicePaymentTerm;
  paymentMethod: InvoicePaymentMethod;
  paymentMethodId: string | undefined;
  method?: any;
  paymentInstallments?: InvoicePaymentInstallment[];
  payments: InvoicePaymentRecepit[];
  isDraft?: boolean;
  paid: boolean;
  toBePaid?: number;

  noVatRateableTotal: number | undefined;
  notTaxableTotal?: number;

  discountAmount?: number;

  productsSaleRateable: number;
  drugsAssignmentRateable: number;
  medicalServicesRateable: number;
  parapharmaceuticalRateable: number;

  advanceItemName?: string;
  advancePaymentAmount?: number;

  redeemedAt?: Date;
  redeemedIn?: Array<{ id: string; amount: number }>;
  redeemedInInvoices?: Invoice[];

  ticket?: {
    id: string;
    number: string;
    customer?: Partial<CustomerLight>;
  };

  sendings?: Array<InvoiceSending>;
  sendingsTS?: InvoiceSending[];
  sendingStatusTS?: InvoiceSendingStatus | undefined;
  sendingStatusMapTS?: Record<string, InvoiceSendingStatus> | undefined;
  sendingErrorMessageMapTS?: Record<string, string>;
  sendingErrorMessageTS?: string | undefined;
  sendingsFE?: InvoiceSending[];
  sendingStatusFE?: InvoiceSendingStatus | undefined;
  sendingStatusMapFE?: Record<string, InvoiceSendingStatus> | undefined;
  sendingErrorMessageMapFE?: Record<string, string>;
  sendingErrorMessageFE?: string | undefined;

  someSendingStatusFEIsAccepted?: boolean;
  someSendingStatusFEIsWarning?: boolean;
  someSendingStatusTSIsAccepted?: boolean;
  someSendingStatusTSIsWarning?: boolean;
  advanceTotalAmount?: number;

  lockedAt?: string;
  locked?: boolean;
  lockedWarning?: boolean;

  storageId?: string;

  xmlExportable: boolean;

  electronicInvoice: boolean;
  tesseraSanitaria?: boolean;

  additionalGeneralData?: InvoiceAdditionalGeneralData;

  origin?: {
    id: string;
    number: string;
    type: "quote" | "invoice";
  };

  expired: boolean;
  expiredAt: string | undefined;
}

export type InvoiceLight = Pick<
  Invoice,
  | "id"
  | "number"
  | "createdAt"
  | "emittedAt"
  | "isDraft"
  | "createdBy"
  | "paidTotal"
  | "redeemedIn"
  | "method"
  | "paid"
  | "adjustedTotal"
  | "type"
  | "emittedBy"
  | "paymentTerm"
  | "expired"
  | "expiredAt"
  | "toBePaid"
  | "customer"
  | "externalId"
>;

export type PartialInvoice = Partial<
  Invoice | { customer: Partial<InvoiceCustomer> }
>;

export type InvoiceItemCreate = {
  hasEnpav: boolean;
  rateTableItemId?: string;
  type?: InvoiceItemType;
  number?: string;
  name?: string;
  description?: string;
  quantity?: number;
  unitPrice?: number;
  unit?: InvoiceItemUnit;
  discount?: number;
  vat?: number;
  vatNature?: VatNatureType;
  adjustment?: number;
  rateTable?: { id: string; name: string };
  ticket?: {
    id: string;
    number: string;
    customer?: Partial<Omit<CustomerLight, "type">>;
  };
  completedBy?: { id: string; name: string };
  bookItemId?: string;
  patientId?: string;
  executedBy: Array<{ id: string; name: string; percentage: number }>;
  executedAt?: string;

  physiciansPercentage: number;

  origin?: {
    id: string;
    number: string;
    type:
      | "quote"
      | "work-order"
      | "health-record"
      | "invoice"
      | "vaccine"
      | "recovery"
      | "prescription";
  };
};

export type InvoiceCreate = Omit<
  Invoice,
  | "id"
  | "number"
  | "invoiceNumber"
  | "rateableTotal"
  | "vatTaxedTotal"
  | "vatTaxAmount"
  | "invoiceRateableTotal"
  | "invoiceTaxedTotal"
  | "invoiceTotal"
  | "enpavRateableTotal"
  | "enpavAmount"
  | "withholdingTaxRateableTotal"
  | "withholdingTaxAmount"
  | "summaryLines"
  | "createdAt"
  | "items"
> &
  Partial<Pick<Invoice, "items">> & {
    origin?: {
      id: string;
      number: string;
      type: "quote";
    };
  };

export type InvoiceAdvanceCreate = InvoiceCreate & { advanceItemName?: string };

export type InvoiceUpdate = Omit<
  Invoice,
  | "id"
  | "number"
  | "invoiceNumber"
  | "rateableTotal"
  | "vatTaxedTotal"
  | "vatTaxAmount"
  | "invoiceRateableTotal"
  | "invoiceTaxedTotal"
  | "invoiceTotal"
  | "enpavRateableTotal"
  | "enpavAmount"
  | "withholdingTaxRateableTotal"
  | "withholdingTaxAmount"
  | "summaryLines"
  | "createdAt"
>;

export function getInvoiceTypeDataSouce({
  ignoreTypes
}: {
  ignoreTypes?: InvoiceType[];
}): Array<{
  display: string;
  value: string;
}> {
  return values(InvoiceType)
    .filter((key) => {
      if (!ignoreTypes || ignoreTypes.length === 0) {
        return true;
      }

      return !ignoreTypes.includes(key);
    })
    .map((key) => ({
      value: key,
      display: sntTranslate(key)
    }));
}

export function getInvoicePaymentMethodDataSource(): Array<{
  display: string;
  value: string;
}> {
  return values(InvoicePaymentMethod).map((key) => ({
    value: key,
    display: sntTranslate(key)
  }));
}

export function getInvoicePaymentTermDataSource(): Array<{
  display: string;
  value: string;
}> {
  return values(InvoicePaymentTerm).map((key) => ({
    value: key,
    display: sntTranslate(key)
  }));
}

export type InvoicePrintViewModelRow = {
  name: string;
  description: string | undefined;
  quantity: number;
  unitPrice: number;
  rateable: number;
  netTotal: number;
  vat: number;
  patientName: string;
  patientMicrochip: string;
  discount: number | undefined;
  enpav: number;
};

export type InvoicePrintedViewModelRow = {
  description: string;
  quantity: number;
  unitPrice: number;
  rateable: number;
  netTotal: number;
  vat: string;
  patientName: string;
  patientMicrochip: string;
  discount: number | undefined;
};

export interface InvoicePrintViewModel {
  documentType: string;

  draft: boolean;

  businessName: string;
  businessAddress: string;
  businessVatCode: string;
  businessFiscalCode: string;

  invoiceNumber: string;
  invoiceDate: string;

  customerNominative: string;
  customerAddressLine1: string;
  customerAddressLine2: string;
  customerVatCode: string;
  customerFiscalCode: string;

  rows: Array<{
    description: string;
    quantity: string;
    unitPrice: string;
    rateable: string;
    netTotal: string;
    vat: string;
    patientName: string;
    patientMicrochip: string;
    discount: string;
  }>;

  vatSummaryLines: Array<{
    total: string;
    tax: string;
    hide: boolean;
  }>;

  vatSplit: boolean;

  discountedTotal: string;
  adjustedTotal: string;

  deadLines: string[];
  paymentInfoLines: string[];
  note: string;

  productsSaleRateable: string;
  drugsAssignmentRateable: string;
  medicalServicesRateable: string;
  parapharmaceuticalRateable: string;

  vatRateableTotal: string;
  rateableTotal: string;
  advanceTotalAmount: string;
  hideRateableTotal: boolean;

  noVatRateableTotal: string;
  notTaxableTotal: string;
  hideNotTaxableTotal: boolean;

  paidTotal: string;
  hidePaidTotal: boolean;

  toBePaid: string;
  hideToBePaid: boolean;

  discount: string;
  hideDiscount: boolean;

  discountAmount: string;

  taxedTotal: string;

  flatRateProfile: boolean;

  adjustment: string;
  hideAdjustement: boolean;

  enpav: string;

  enpavAmount: string;
  hideEnpavAmount: boolean;

  withholdingTax: string;

  withholdingTaxAmount: string;
  hideWithholdingTaxAmount: boolean;

  footerName: string;

  paid: boolean;

  paymentMethod: string;
  paymentMethodValue: string;

  paymentTerm: string;

  payments: Array<{
    date: string;
    amount: string;
    method: string;
  }>;

  iban: string;

  hideStampTax: boolean;
  stampTax: string;

  hideVat: boolean;

  hideAdditionalData: boolean;
  additionalDataType: string;
  additionalDataDocumentNumber: string;
  additionalDataDocumentDate: string;
  additionalDataContractOrAgreementCode: string;
  additionalDataCig: string;
  additionalDataCup: string;

  hasVatNotice: boolean;
  vatNoticeN21: string;
  vatNoticeN22: string;
  vatNoticeN31: string;
  vatNoticeN32: string;
  vatNoticeN33: string;
  reversedInvoiceNumber?: string;
  reversedInvoiceAt?: string;

  taxesAmount: string;

  notulaLabel: string;
  dataLabel: string;
  tipoLabel: string;
  numeroDocumentoLabel: string;
  dataDocumentoLabel: string;
  commessaConvenzioneLabel: string;
  spettabileLabel: string;
  pivaLabel: string;
  cfLabel: string;
  descrizioneLabel: string;
  qtaLabel: string;
  discountLabel: string;
  pUnitarioLabel: string;
  totaleLabel: string;
  ivaLabel: string;
  pazienteLabel: string;
  prestazioniLabel: string;
  prodottiLabel: string;
  farmaciLabel: string;
  parafarmaciLabel: string;
  accontoLabel: string;
  imponibileLabel: string;
  nonImponibileLabel: string;
  totaleFatturaLabel: string;
  arrotondamentoLabel: string;
  totaleDaPagareLabel: string;
  incassatoLabel: string;
  residuoLabel: string;
  incassiLabel: string;
  terminiDiPagamentoLabel: string;
  noteLabel: string;
  pagamentoIndirizzatoALabel: string;
  questaNotaDiCreditoRettificaLaFatturaLabel: string;
  numeroLabel: string;
  emessaIlLabel: string;

  enableTaxInvoiceLabel: boolean;
  enableCustomerAddress: boolean;

  disableVatSplitNotice: boolean;
  disableFlatRateProfileNotice: boolean;
  disableCodeCig: boolean;
  disableCodeCup: boolean;
  disableEnpavAmount: boolean;
  disableRitenutaDAcconto: boolean;
  disableImpostaDiBollo: boolean;

  isInvoiceCompleteDetails: boolean;
}

export interface InvoicePaymentReceiptPrintViewModel extends InvoicePrintViewModel {
  paymentReceipts: Array<{
    amountCashedIn: string;
    description: string;
    date: string;
  }>;

  paymentReceiptLabel: string;
  paymentMethodLabel: string;
  dateLabel: string;
  amountCashedInLabel: string;
}

export function invoiceElectronicInvoiceCanBeDeactivated(
  fiscalProfile: FiscalProfile,
  invoice: Invoice
): boolean {
  return (
    invoice.electronicInvoice &&
    !!fiscalProfile.electronicInvoice &&
    !fiscalProfile.electronicInvoice.mandatory
  );
}

export function bookItemToInvoiceItemCreate(
  bookItem: BookItem,
  itemType: InvoiceItemType,
  patientId: string | undefined,
  rateTable: RateTableLight | undefined,
  medicalService: MedicalServiceLight | undefined
): InvoiceItemCreate {
  return {
    ...bookItemCreateToInvoiceItemCreate({
      bookItemCreate: <any>bookItem,
      itemType,
      patientId,
      medicalService,
      rateTable
    }),
    bookItemId: bookItem.id
  };
}

export function bookItemCreateToInvoiceItemCreate({
  bookItemCreate,
  itemType,
  patientId,
  rateTable,
  medicalService
}: {
  bookItemCreate: BookItemCreate;
  itemType: InvoiceItemType;
  patientId: string | undefined;
  rateTable: RateTableLight | undefined;
  medicalService: MedicalServiceLight | undefined;
}): InvoiceItemCreate {
  let physiciansPercentage = 0;

  switch (itemType) {
    case InvoiceItemType.MEDICAL_SERVICE: {
      physiciansPercentage =
        isUndefined(medicalService?.physiciansPercentage) ||
        isNull(medicalService?.physiciansPercentage)
          ? rateTable?.serviceModifier?.physiciansPercentage || 0
          : medicalService.physiciansPercentage;
      break;
    }
    case InvoiceItemType.DRUG_ASSIGNMENT: {
      physiciansPercentage = rateTable?.drugModifier?.physiciansPercentage || 0;
      break;
    }
    case InvoiceItemType.PRODUCT_SALE: {
      physiciansPercentage =
        rateTable?.productModifier?.physiciansPercentage || 0;
      break;
    }
    case InvoiceItemType.ADVANCE_PAYMENT: {
      physiciansPercentage = rateTable
        ? rateTable.serviceModifier?.physiciansPercentage || 0
        : 0;
      break;
    }
  }

  return {
    physiciansPercentage,
    hasEnpav: !!bookItemCreate.hasEnpav,
    name: bookItemCreate.name,
    description: bookItemCreate.description,
    number: bookItemCreate.number,
    quantity: bookItemCreate.quantity,
    rateTable: bookItemCreate.rateTable
      ? {
          id: bookItemCreate.rateTable.id,
          name: bookItemCreate.rateTable.name
        }
      : bookItemCreate.rateTable,
    rateTableItemId: bookItemCreate.rateTableItemId,
    discount: bookItemCreate.discount,
    type: itemType,
    unitPrice: bookItemCreate.unitPrice,
    vat: bookItemCreate.vat,
    vatNature: bookItemCreate.vatNature,
    patientId,
    ticket: bookItemCreate.ticket
      ? {
          id: bookItemCreate.ticket!.id,
          number: bookItemCreate.ticket!.number,
          customer: bookItemCreate.ticket!.customer
        }
      : undefined,
    bookItemId: bookItemCreate.bookItemId
      ? bookItemCreate.bookItemId
      : undefined,
    executedBy: bookItemCreate.executedBy,
    executedAt: bookItemCreate.executedAt,
    unit: bookItemUnitToInvoiceItemUnit(bookItemCreate.unit),
    origin: bookItemCreate?.origin
  };
}

export function bookItemTypeToInvoiceItemType(
  bookItemType: BookItemType
): InvoiceItemType {
  switch (bookItemType) {
    case BookItemType.MEDICAL_SERVICE:
      return InvoiceItemType.MEDICAL_SERVICE;
    case BookItemType.DRUG_ASSIGNMENT:
      return InvoiceItemType.DRUG_ASSIGNMENT;
    case BookItemType.PRODUCT_SALE:
      return InvoiceItemType.PRODUCT_SALE;
    case BookItemType.PARAPHARMACEUTICAL:
      return InvoiceItemType.PARAPHARMACEUTICAL;
    case BookItemType.ADVANCE_PAYMENT:
      return InvoiceItemType.ADVANCE_PAYMENT;
  }
}

export function invoiceGetExpiredAt(invoice: InvoiceLight): string | undefined {
  if (!invoice.emittedAt) {
    return undefined;
  }

  switch (invoice.paymentTerm) {
    case InvoicePaymentTerm.ADVANCE_PAYMENT: {
      return undefined;
    }
    case InvoicePaymentTerm.DELAYED_PAYMENT: {
      return undefined;
    }
    case InvoicePaymentTerm.NINETY_DAYS_PAYMENT: {
      return dateUtil(invoice.emittedAt).add(90, "days").toISOString();
    }
    case InvoicePaymentTerm.SIXTY_DAYS_PAYMENT: {
      return dateUtil(invoice.emittedAt).add(60, "days").toISOString();
    }
    case InvoicePaymentTerm.THIRTY_DAYS_PAYMENT: {
      return dateUtil(invoice.emittedAt).add(30, "days").toISOString();
    }
    case InvoicePaymentTerm.DIRECT_REMITTACE: {
      return invoice.emittedAt;
    }
    case InvoicePaymentTerm.INSTALLMENT_PLAN: {
      return undefined;
    }
  }
}

export function invoiceIsExpired(invoice: InvoiceLight): boolean {
  switch (invoice.paymentTerm) {
    case InvoicePaymentTerm.ADVANCE_PAYMENT: {
      return !invoice.paid && !!invoice.emittedAt;
    }
    case InvoicePaymentTerm.DELAYED_PAYMENT: {
      return false;
    }
    case InvoicePaymentTerm.NINETY_DAYS_PAYMENT: {
      return (
        !invoice.paid &&
        !!invoice.emittedAt &&
        dateUtil(invoice.emittedAt).add(90, "days").isAfter(dateUtil())
      );
    }
    case InvoicePaymentTerm.SIXTY_DAYS_PAYMENT: {
      return (
        !invoice.paid &&
        !!invoice.emittedAt &&
        dateUtil(invoice.emittedAt).add(60, "days").isAfter(dateUtil())
      );
    }
    case InvoicePaymentTerm.THIRTY_DAYS_PAYMENT: {
      return (
        !invoice.paid &&
        !!invoice.emittedAt &&
        dateUtil(invoice.emittedAt).add(30, "days").isAfter(dateUtil())
      );
    }
    case InvoicePaymentTerm.DIRECT_REMITTACE: {
      return !invoice.paid && !!invoice.emittedAt;
    }
    case InvoicePaymentTerm.INSTALLMENT_PLAN: {
      return !invoice.paid && !!invoice.emittedAt;
    }
  }
}
